import type { NbaRatesPlayerStats, NbaRatesAggregateStats, ColumnDef, CategoryTab, AggSummaryItem } from './types';

// ─── Formatting ──────────────────────────────────────────────────────────────

/** Handles both decimal (0.55) and whole number (55.0) percentage formats */
export const formatPct = (v: number | null | undefined): string => {
  if (v == null) return '-';
  const pct = v > 1 ? v : v * 100;
  return pct.toFixed(1);
};

export const formatInt = (v: number | null | undefined): string =>
  v != null ? Math.round(v).toString() : '-';

export const formatDec1 = (v: number | null | undefined): string =>
  v != null ? v.toFixed(1) : '-';

export const formatDec2 = (v: number | null | undefined): string =>
  v != null ? v.toFixed(2) : '-';

export const formatPlusMinus = (v: number | null | undefined): string => {
  if (v == null) return '-';
  const rounded = Math.round(v);
  return rounded > 0 ? `+${rounded}` : `${rounded}`;
};

// ─── Safe Math ───────────────────────────────────────────────────────────────

/** Returns null when divisor is 0 to prevent divide-by-zero */
export const safeDivide = (a: number, b: number): number | null =>
  b === 0 ? null : a / b;

// ─── Fantasy Points ──────────────────────────────────────────────────────────

/** DraftKings NBA fantasy points (no DD/TD bonus - can't track from aggregate data) */
export const calcDkFpts = (player: NbaRatesPlayerStats): number => {
  const pts = player.pts || 0;
  const reb = player.reb || 0;
  const ast = player.ast || 0;
  const stl = player.stl || 0;
  const blk = player.blk || 0;
  const tov = player.tov || 0;
  const fg3m = player.fg3m || 0;
  return pts + (reb * 1.25) + (ast * 1.5) + (stl * 2) + (blk * 2) - (tov * 0.5) + (fg3m * 0.5);
};

/** Fantasy points per minute played */
export const calcFptsPerMin = (player: NbaRatesPlayerStats): number | null => {
  if (!player.minutes || player.minutes === 0) return null;
  return calcDkFpts(player) / player.minutes;
};

/** FanDuel NBA fantasy points */
export const calcFdFpts = (player: NbaRatesPlayerStats): number => {
  const pts = player.pts || 0;
  const reb = player.reb || 0;
  const ast = player.ast || 0;
  const stl = player.stl || 0;
  const blk = player.blk || 0;
  const tov = player.tov || 0;
  return pts + (reb * 1.2) + (ast * 1.5) + (stl * 3) + (blk * 3) - tov;
};

// ─── Per-100 Possessions ─────────────────────────────────────────────────────

export const calcPer100 = (value: number, possessions: number): number | null =>
  safeDivide(value * 100, possessions);

export const calcPer36 = (value: number, minutes: number): number | null =>
  minutes === 0 ? null : (value / minutes) * 36;

export const calcPer1 = (value: number, minutes: number): number | null =>
  minutes === 0 ? null : value / minutes;

/** FanDuel fantasy points per minute played */
export const calcFdFptsPerMin = (player: NbaRatesPlayerStats): number | null => {
  if (!player.minutes || player.minutes === 0) return null;
  return calcFdFpts(player) / player.minutes;
};

/** Get the right possession count based on stat type (offensive or defensive) */
export const getPossessions = (player: NbaRatesPlayerStats, col: ColumnDef): number => {
  if (col.possType === 'def') {
    return player.def_poss || player.possessions || 1;
  }
  return player.off_poss || player.possessions || 1;
};

// ─── Delta Highlighting ──────────────────────────────────────────────────────

/** Minimum relative change (%) before highlighting kicks in */
export const RELATIVE_THRESHOLD = 7;

/** Maximum relative change (%) for full-intensity highlighting */
const RELATIVE_MAX = 30;

/**
 * Compute percentage change from baseline: ((filtered - baseline) / |baseline|) * 100.
 * Returns null when baseline is zero or either value is null.
 */
export const calcRelativeDelta = (
  filteredValue: number | null,
  baselineValue: number | null,
): number | null => {
  if (filteredValue === null || baselineValue === null || baselineValue === 0) return null;
  return ((filteredValue - baselineValue) / Math.abs(baselineValue)) * 100;
};

/**
 * Get background color based on relative delta (% change from baseline).
 * Green = above baseline, Red = below baseline.
 * Shading starts at ±10% and reaches full intensity at ±50%.
 */
export const getDeltaBackground = (relativeDelta: number | null): string => {
  if (relativeDelta === null || Math.abs(relativeDelta) < RELATIVE_THRESHOLD) {
    return 'transparent';
  }
  const magnitude = Math.min(Math.abs(relativeDelta), RELATIVE_MAX);
  const opacity = 0.08 + ((magnitude - RELATIVE_THRESHOLD) / (RELATIVE_MAX - RELATIVE_THRESHOLD)) * 0.27;

  return relativeDelta > 0
    ? `rgba(16, 185, 129, ${opacity})`
    : `rgba(239, 68, 68, ${opacity})`;
};

// ─── Cell Value Calculation ──────────────────────────────────────────────────

/**
 * Get the display value for a cell, accounting for display mode (totals, per100, per36, rates).
 * The displayMode param accepts both old values ('totals','per100','rates','custom')
 * and new RateMode values ('totals','per100','per36').
 */
export const getCellValue = (
  player: NbaRatesPlayerStats,
  key: string,
  col: ColumnDef,
  displayMode: string,
): number | null => {
  // ── Calculated stats ─────────────────────────────────────────────────
  if (key === 'dk_fpts') {
    const fpts = calcDkFpts(player);
    if (displayMode === 'per100' && (col.isPer100 || col.isCountingStat)) {
      return calcPer100(fpts, getPossessions(player, col));
    }
    if (displayMode === 'per36' && col.isCountingStat) {
      return calcPer36(fpts, player.minutes || 0);
    }
    if (displayMode === 'per1' && col.isCountingStat) {
      return calcPer1(fpts, player.minutes || 0);
    }
    return fpts;
  }
  if (key === 'fd_fpts') {
    const fpts = calcFdFpts(player);
    if (displayMode === 'per100' && (col.isPer100 || col.isCountingStat)) {
      return calcPer100(fpts, getPossessions(player, col));
    }
    if (displayMode === 'per36' && col.isCountingStat) {
      return calcPer36(fpts, player.minutes || 0);
    }
    if (displayMode === 'per1' && col.isCountingStat) {
      return calcPer1(fpts, player.minutes || 0);
    }
    return fpts;
  }
  if (key === 'fpts_per_min') {
    return calcFptsPerMin(player);
  }
  if (key === 'fd_fpts_per_min') {
    return calcFdFptsPerMin(player);
  }

  // ── Raw stats ────────────────────────────────────────────────────────
  const rawValue = player[key as keyof NbaRatesPlayerStats] as number | null;
  if (rawValue == null) return null;

  // Per-100 possessions
  if (displayMode === 'per100' && (col.isPer100 || col.isCountingStat)) {
    return calcPer100(rawValue, getPossessions(player, col));
  }

  // Per-36 minutes
  if (displayMode === 'per36' && col.isCountingStat) {
    return calcPer36(rawValue, player.minutes || 0);
  }

  // Per-1 minute
  if (displayMode === 'per1' && col.isCountingStat) {
    return calcPer1(rawValue, player.minutes || 0);
  }

  return rawValue;
};

// ─── Aggregate Stat Formatting ───────────────────────────────────────────────

export const formatAggValue = (key: string, value: unknown): string => {
  if (value == null) return '-';
  const v = value as number;
  if (key === 'net_rtg' || key.includes('net_rtg')) {
    return (v > 0 ? '+' : '') + v.toFixed(1);
  }
  if (key.includes('pct') || key === 'usage') {
    return formatPct(v);
  }
  return v.toFixed(1);
};

// ─── Column Definitions ──────────────────────────────────────────────────────

export const TOTALS_COLUMNS: ColumnDef[] = [
  { key: 'minutes', label: 'MIN', format: formatDec1, group: 'time' },
  { key: 'off_poss', label: 'POSS', format: formatInt, group: 'time' },
  { key: 'dk_fpts', label: 'FPTS', format: formatDec1, isCalculated: true, group: 'fantasy' },
  { key: 'fpts_per_min', label: 'FP/M', format: formatDec2, isCalculated: true, group: 'fantasy' },
  { key: 'pts', label: 'PTS', format: formatInt, group: 'scoring' },
  { key: 'reb', label: 'REB', format: formatInt, group: 'misc' },
  { key: 'ast', label: 'AST', format: formatInt, group: 'misc' },
  { key: 'stl', label: 'STL', format: formatInt, group: 'misc' },
  { key: 'blk', label: 'BLK', format: formatInt, group: 'misc' },
  { key: 'tov', label: 'TOV', format: formatInt, group: 'misc' },
  { key: 'fg2m', label: '2PM', format: formatInt, group: 'shooting' },
  { key: 'fg2a', label: '2PA', format: formatInt, group: 'shooting' },
  { key: 'fg3m', label: '3PM', format: formatInt, group: 'shooting' },
  { key: 'fg3a', label: '3PA', format: formatInt, group: 'shooting' },
  { key: 'ftm', label: 'FTM', format: formatInt, group: 'shooting' },
  { key: 'fta', label: 'FTA', format: formatInt, group: 'shooting' },
  { key: 'oreb', label: 'OREB', format: formatInt, group: 'rebounding' },
  { key: 'dreb', label: 'DREB', format: formatInt, group: 'rebounding' },
];

export const PER100_COLUMNS: ColumnDef[] = [
  { key: 'minutes', label: 'MIN', format: formatDec1, group: 'time' },
  { key: 'off_poss', label: 'POSS', format: formatInt, group: 'time' },
  { key: 'dk_fpts', label: 'FPTS', format: formatDec1, isPer100: true, isCalculated: true, group: 'fantasy', possType: 'off' },
  { key: 'fpts_per_min', label: 'FP/M', format: formatDec2, isCalculated: true, group: 'fantasy' },
  { key: 'fg2a', label: '2PA', format: formatDec1, isPer100: true, group: 'shooting', possType: 'off' },
  { key: 'fg3a', label: '3PA', format: formatDec1, isPer100: true, group: 'shooting', possType: 'off' },
  { key: 'fta', label: 'FTA', format: formatDec1, isPer100: true, group: 'shooting', possType: 'off' },
  { key: 'ast', label: 'AST', format: formatDec1, isPer100: true, group: 'playmaking', possType: 'off' },
  { key: 'oreb', label: 'OREB', format: formatDec1, isPer100: true, group: 'rebounding', possType: 'off' },
  { key: 'dreb', label: 'DREB', format: formatDec1, isPer100: true, group: 'rebounding', possType: 'def' },
  { key: 'stl', label: 'STL', format: formatDec1, isPer100: true, group: 'defense', possType: 'def' },
  { key: 'blk', label: 'BLK', format: formatDec1, isPer100: true, group: 'defense', possType: 'def' },
  { key: 'tov', label: 'TOV', format: formatDec1, isPer100: true, group: 'misc', possType: 'off' },
  { key: 'pts', label: 'PTS', format: formatDec1, isPer100: true, group: 'scoring', possType: 'off' },
];

export const RATES_COLUMNS: ColumnDef[] = [
  { key: 'minutes', label: 'MIN', format: formatDec1, group: 'time' },
  { key: 'usage', label: 'USG', format: formatPct, isPct: true, group: 'usage' },
  { key: 'ts_pct', label: 'TS', format: formatPct, isPct: true, group: 'efficiency' },
  { key: 'efg_pct', label: 'eFG', format: formatPct, isPct: true, group: 'efficiency' },
  { key: 'fg2_pct', label: '2P', format: formatPct, isPct: true, group: 'shooting' },
  { key: 'fg3_pct', label: '3P', format: formatPct, isPct: true, group: 'shooting' },
  { key: 'ft_pct', label: 'FT', format: formatPct, isPct: true, group: 'shooting' },
  { key: 'ast_ratio', label: 'AST', format: formatPct, isPct: true, group: 'playmaking' },
  { key: 'tov_pct', label: 'TOV', format: formatPct, isPct: true, group: 'playmaking' },
  { key: 'off_reb_pct', label: 'ORB', format: formatPct, isPct: true, group: 'rebounding' },
  { key: 'def_reb_pct', label: 'DRB', format: formatPct, isPct: true, group: 'rebounding' },
];

export const COLUMNS_BY_MODE: Record<string, ColumnDef[]> = {
  totals: TOTALS_COLUMNS,
  per100: PER100_COLUMNS,
  rates: RATES_COLUMNS,
};

// ─── Category Tab Column Definitions ────────────────────────────────────────

export const STOKASTIC_COLUMNS: ColumnDef[] = [
  { key: 'minutes', label: 'MIN', format: formatDec1, group: 'time' },
  { key: 'off_poss', label: 'POSS', format: formatInt, group: 'time' },
  { key: 'dk_fpts', label: 'FPTS', format: formatDec1, formatRate: formatDec1, isCountingStat: true, isCalculated: true, group: 'fantasy', possType: 'off' },
  { key: 'fpts_per_min', label: 'FP/M', format: formatDec2, isCalculated: true, group: 'fantasy' },
  { key: 'usage', label: 'USG', format: formatPct, isPct: true, group: 'usage' },
  { key: 'pts', label: 'PTS', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'scoring', possType: 'off' },
  { key: 'reb', label: 'REB', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'misc', possType: 'off' },
  { key: 'ast', label: 'AST', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'misc', possType: 'off' },
  { key: 'stl', label: 'STL', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'misc', possType: 'def' },
  { key: 'blk', label: 'BLK', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'misc', possType: 'def' },
  { key: 'tov', label: 'TOV', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'misc', possType: 'off' },
  { key: 'ts_pct', label: 'TS', format: formatPct, isPct: true, group: 'efficiency' },
  { key: 'efg_pct', label: 'eFG', format: formatPct, isPct: true, group: 'efficiency' },
];

export const PROPS_COLUMNS: ColumnDef[] = [
  { key: 'minutes', label: 'MIN', format: formatDec1, group: 'time' },
  { key: 'off_poss', label: 'POSS', format: formatInt, group: 'time' },
  { key: 'usage', label: 'USG', format: formatPct, isPct: true, group: 'usage' },
  { key: 'pts', label: 'PTS', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'scoring', possType: 'off' },
  { key: 'reb', label: 'REB', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'misc', possType: 'off' },
  { key: 'ast', label: 'AST', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'misc', possType: 'off' },
  { key: 'fg3m', label: '3PM', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shooting', possType: 'off' },
];

export const SHOOTING_COLUMNS: ColumnDef[] = [
  { key: 'minutes', label: 'MIN', format: formatDec1, group: 'time' },
  { key: 'off_poss', label: 'POSS', format: formatInt, group: 'time' },
  { key: 'pts', label: 'PTS', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'scoring', possType: 'off' },
  { key: 'fg2m', label: '2PM', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shooting', possType: 'off' },
  { key: 'fg2a', label: '2PA', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shooting', possType: 'off' },
  { key: 'fg2_pct', label: '2P', format: formatPct, isPct: true, group: 'shooting' },
  { key: 'fg3m', label: '3PM', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shooting', possType: 'off' },
  { key: 'fg3a', label: '3PA', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shooting', possType: 'off' },
  { key: 'fg3_pct', label: '3P', format: formatPct, isPct: true, group: 'shooting' },
  { key: 'ftm', label: 'FTM', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shooting', possType: 'off' },
  { key: 'fta', label: 'FTA', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shooting', possType: 'off' },
  { key: 'ft_pct', label: 'FT', format: formatPct, isPct: true, group: 'shooting' },
  { key: 'ts_pct', label: 'TS', format: formatPct, isPct: true, group: 'efficiency' },
  { key: 'efg_pct', label: 'eFG', format: formatPct, isPct: true, group: 'efficiency' },
  { key: 'ftr', label: 'FTr', format: formatDec2, group: 'efficiency' },
];

export const SHOT_ZONES_COLUMNS: ColumnDef[] = [
  { key: 'minutes', label: 'MIN', format: formatDec1, group: 'time' },
  { key: 'off_poss', label: 'POSS', format: formatInt, group: 'time' },
  // At Rim
  { key: 'at_rim_fga', label: 'Rim FGA', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'at_rim', possType: 'off' },
  { key: 'at_rim_fgm', label: 'Rim FGM', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'at_rim', possType: 'off' },
  { key: 'at_rim_pct', label: 'Rim%', format: formatPct, isPct: true, group: 'at_rim' },
  { key: 'at_rim_freq', label: 'Rim FR', format: formatPct, isPct: true, group: 'at_rim' },
  // Short Mid
  { key: 'short_mid_fga', label: 'SM FGA', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'short_mid', possType: 'off' },
  { key: 'short_mid_fgm', label: 'SM FGM', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'short_mid', possType: 'off' },
  { key: 'short_mid_pct', label: 'SM%', format: formatPct, isPct: true, group: 'short_mid' },
  { key: 'short_mid_freq', label: 'SM FR', format: formatPct, isPct: true, group: 'short_mid' },
  // Long Mid
  { key: 'long_mid_fga', label: 'LM FGA', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'long_mid', possType: 'off' },
  { key: 'long_mid_fgm', label: 'LM FGM', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'long_mid', possType: 'off' },
  { key: 'long_mid_pct', label: 'LM%', format: formatPct, isPct: true, group: 'long_mid' },
  { key: 'long_mid_freq', label: 'LM FR', format: formatPct, isPct: true, group: 'long_mid' },
  // Corner 3
  { key: 'corner3_fga', label: 'C3 FGA', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'corner3', possType: 'off' },
  { key: 'corner3_fgm', label: 'C3 FGM', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'corner3', possType: 'off' },
  { key: 'corner3_pct', label: 'C3%', format: formatPct, isPct: true, group: 'corner3' },
  { key: 'corner3_freq', label: 'C3 FR', format: formatPct, isPct: true, group: 'corner3' },
  // Arc 3
  { key: 'arc3_fga', label: 'A3 FGA', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'arc3', possType: 'off' },
  { key: 'arc3_fgm', label: 'A3 FGM', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'arc3', possType: 'off' },
  { key: 'arc3_pct', label: 'A3%', format: formatPct, isPct: true, group: 'arc3' },
  { key: 'arc3_freq', label: 'A3 FR', format: formatPct, isPct: true, group: 'arc3' },
];

export const REBOUNDING_COLUMNS: ColumnDef[] = [
  { key: 'minutes', label: 'MIN', format: formatDec1, group: 'time' },
  { key: 'off_poss', label: 'POSS', format: formatInt, group: 'time' },
  { key: 'reb', label: 'REB', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'rebounding', possType: 'off' },
  { key: 'oreb', label: 'OREB', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'rebounding', possType: 'off' },
  { key: 'dreb', label: 'DREB', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'rebounding', possType: 'def' },
  { key: 'second_chance_pts', label: '2C PTS', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'rebounding', possType: 'off' },
  { key: 'off_reb_pct', label: 'ORB%', format: formatPct, isPct: true, group: 'rebounding' },
  { key: 'def_reb_pct', label: 'DRB%', format: formatPct, isPct: true, group: 'rebounding' },
  { key: 'self_oreb_pct', label: 'SORB%', format: formatPct, isPct: true, group: 'rebounding' },
];

export const DEFENSE_COLUMNS: ColumnDef[] = [
  { key: 'minutes', label: 'MIN', format: formatDec1, group: 'time' },
  { key: 'off_poss', label: 'POSS', format: formatInt, group: 'time' },
  { key: 'stl', label: 'STL', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'defense', possType: 'def' },
  { key: 'blk', label: 'BLK', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'defense', possType: 'def' },
  { key: 'recovered_blocks', label: 'RBLK', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'defense', possType: 'def' },
  { key: 'fouls', label: 'PF', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'defense', possType: 'def' },
  { key: 'fouls_drawn', label: 'FD', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'defense', possType: 'off' },
  { key: 'def_rtg', label: 'DRtg', format: formatDec1, group: 'defense' },
];

export const PLAYMAKING_COLUMNS: ColumnDef[] = [
  { key: 'minutes', label: 'MIN', format: formatDec1, group: 'time' },
  { key: 'off_poss', label: 'POSS', format: formatInt, group: 'time' },
  { key: 'ast', label: 'AST', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'playmaking', possType: 'off' },
  { key: 'two_pt_assists', label: '2P AST', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'playmaking', possType: 'off' },
  { key: 'three_pt_assists', label: '3P AST', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'playmaking', possType: 'off' },
  { key: 'assist_points', label: 'AST PTS', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'playmaking', possType: 'off' },
  { key: 'tov', label: 'TOV', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'playmaking', possType: 'off' },
  { key: 'bad_pass_tov', label: 'BPTOV', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'playmaking', possType: 'off' },
  { key: 'lost_ball_tov', label: 'LBTOV', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'playmaking', possType: 'off' },
  { key: 'ast_ratio', label: 'AST%', format: formatPct, isPct: true, group: 'playmaking' },
  { key: 'tov_pct', label: 'TOV%', format: formatPct, isPct: true, group: 'playmaking' },
  { key: 'ast_tov_ratio', label: 'A/TO', format: formatDec1, group: 'playmaking' },
];

export const COLUMNS_BY_CATEGORY: Record<CategoryTab, ColumnDef[]> = {
  stokastic: STOKASTIC_COLUMNS,
  props: PROPS_COLUMNS,
  shooting: SHOOTING_COLUMNS,
  shotZones: SHOT_ZONES_COLUMNS,
  rebounding: REBOUNDING_COLUMNS,
  defense: DEFENSE_COLUMNS,
  playmaking: PLAYMAKING_COLUMNS,
  custom: [], // handled separately via custom column picker
};

// ─── Category Tab Labels ────────────────────────────────────────────────────

export const CATEGORY_TAB_LABELS: Record<CategoryTab, string> = {
  stokastic: 'Stokastic',
  props: 'Props',
  shooting: 'Shooting',
  shotZones: 'Shot Zones',
  rebounding: 'Rebounding',
  defense: 'Defense',
  playmaking: 'Playmaking',
  custom: 'Custom',
};

export const CATEGORY_TAB_ORDER: CategoryTab[] = [
  'stokastic', 'props', 'shooting', 'shotZones', 'rebounding', 'defense', 'playmaking', 'custom',
];

// ─── Contextual Aggregate Summary Configs ───────────────────────────────────

const getNetRtgColor = (value: number | null): string => {
  if (value === null) return '#6B7280';
  if (value >= 8) return '#34D399';
  if (value >= 3) return '#10B981';
  if (value <= -8) return '#F87171';
  if (value <= -3) return '#EF4444';
  return '#E8EAED';
};

const summaryMin: AggSummaryItem = {
  key: 'minutes', label: 'MIN',
  format: (agg) => formatDec1(agg.minutes),
};
const summaryPoss: AggSummaryItem = {
  key: 'off_poss', label: 'POSS',
  format: (agg) => formatInt(agg.off_poss),
};
const summaryORtg: AggSummaryItem = {
  key: 'off_rtg', label: 'ORtg',
  format: (agg) => formatAggValue('off_rtg', agg.off_rtg),
};
const summaryDRtg: AggSummaryItem = {
  key: 'def_rtg', label: 'DRtg',
  format: (agg) => formatAggValue('def_rtg', agg.def_rtg),
};
const summaryNet: AggSummaryItem = {
  key: 'net_rtg', label: 'NET',
  format: (agg) => formatAggValue('net_rtg', agg.net_rtg),
  valueColor: (agg) => getNetRtgColor(agg.net_rtg),
  bold: true,
};
const summaryTS: AggSummaryItem = {
  key: 'ts_pct', label: 'TS%',
  format: (agg) => agg.ts_pct != null ? `${formatPct(agg.ts_pct)}%` : '-',
};
const summaryEFG: AggSummaryItem = {
  key: 'efg_pct', label: 'eFG%',
  format: (agg) => agg.efg_pct != null ? `${formatPct(agg.efg_pct)}%` : '-',
};
const summary2P: AggSummaryItem = {
  key: 'fg2_pct', label: '2P%',
  format: (agg) => agg.fg2_pct != null ? `${formatPct(agg.fg2_pct)}%` : '-',
};
const summary3P: AggSummaryItem = {
  key: 'fg3_pct', label: '3P%',
  format: (agg) => agg.fg3_pct != null ? `${formatPct(agg.fg3_pct)}%` : '-',
};

export const AGG_SUMMARY_BY_CATEGORY: Record<CategoryTab, AggSummaryItem[]> = {
  stokastic: [summaryMin, summaryPoss, summaryORtg, summaryDRtg, summaryNet, summaryTS, summaryEFG],
  props: [summaryMin, summaryPoss, summaryORtg, summaryDRtg, summaryNet],
  shooting: [summaryMin, summaryPoss, summary2P, summary3P, summaryTS, summaryEFG],
  shotZones: [summaryMin, summaryPoss, summaryEFG, summaryTS],
  rebounding: [summaryMin, summaryPoss, summaryORtg, summaryDRtg, summaryNet],
  defense: [summaryMin, summaryPoss, summaryDRtg, summaryNet],
  playmaking: [summaryMin, summaryPoss, summaryORtg, summaryNet],
  custom: [summaryMin, summaryPoss, summaryORtg, summaryDRtg, summaryNet, summaryTS, summaryEFG],
};

// ─── Custom Column Catalog ──────────────────────────────────────────────────

export interface CustomStatOption {
  key: string;
  label: string;
  description: string;
  category: string;
  columnDef: ColumnDef;
}

export const MAX_CUSTOM_COLUMNS = 15;

export const CUSTOM_STAT_CATEGORIES = [
  'General',
  'Scoring',
  'Shooting Percentages',
  'At Rim',
  'Mid Range',
  'Three Point Zones',
  'Efficiency',
  'Playmaking',
  'Rebounding',
  'Defense',
  'Fouls',
  'Turnovers',
  'Second Chance',
  'Fantasy',
] as const;

export const CUSTOM_STAT_OPTIONS: CustomStatOption[] = [
  // ── General ─────────────────────────────────────────────────────────────
  { key: 'minutes', label: 'MIN', description: 'Minutes played', category: 'General',
    columnDef: { key: 'minutes', label: 'MIN', format: formatDec1, group: 'time' } },
  { key: 'off_poss', label: 'POSS', description: 'Offensive possessions', category: 'General',
    columnDef: { key: 'off_poss', label: 'POSS', format: formatInt, group: 'time' } },
  { key: 'plus_minus', label: '+/-', description: 'Plus/minus point differential', category: 'General',
    columnDef: { key: 'plus_minus', label: '+/-', format: formatPlusMinus, group: 'general' } },

  // ── Scoring ─────────────────────────────────────────────────────────────
  { key: 'fga', label: 'FGA', description: 'Total field goal attempts', category: 'Scoring',
    columnDef: { key: 'fga', label: 'FGA', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shooting', possType: 'off' } },
  { key: 'fgm', label: 'FGM', description: 'Total field goals made', category: 'Scoring',
    columnDef: { key: 'fgm', label: 'FGM', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shooting', possType: 'off' } },
  { key: 'pts', label: 'PTS', description: 'Points', category: 'Scoring',
    columnDef: { key: 'pts', label: 'PTS', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'scoring', possType: 'off' } },
  { key: 'fg2m', label: '2PM', description: '2-point field goals made', category: 'Scoring',
    columnDef: { key: 'fg2m', label: '2PM', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shooting', possType: 'off' } },
  { key: 'fg2a', label: '2PA', description: '2-point field goals attempted', category: 'Scoring',
    columnDef: { key: 'fg2a', label: '2PA', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shooting', possType: 'off' } },
  { key: 'fg3m', label: '3PM', description: '3-point field goals made', category: 'Scoring',
    columnDef: { key: 'fg3m', label: '3PM', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shooting', possType: 'off' } },
  { key: 'fg3a', label: '3PA', description: '3-point field goals attempted', category: 'Scoring',
    columnDef: { key: 'fg3a', label: '3PA', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shooting', possType: 'off' } },
  { key: 'ftm', label: 'FTM', description: 'Free throws made', category: 'Scoring',
    columnDef: { key: 'ftm', label: 'FTM', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shooting', possType: 'off' } },
  { key: 'fta', label: 'FTA', description: 'Free throws attempted', category: 'Scoring',
    columnDef: { key: 'fta', label: 'FTA', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shooting', possType: 'off' } },
  { key: 'assist_points', label: 'AST PTS', description: 'Total points created via assists', category: 'Scoring',
    columnDef: { key: 'assist_points', label: 'AST PTS', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'scoring', possType: 'off' } },
  { key: 'second_chance_pts', label: '2nd PTS', description: 'Second chance points (after offensive rebound)', category: 'Scoring',
    columnDef: { key: 'second_chance_pts', label: '2nd PTS', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'scoring', possType: 'off' } },

  // ── Shooting Percentages ────────────────────────────────────────────────
  { key: 'fg_pct', label: 'FG%', description: 'Overall field goal percentage', category: 'Shooting Percentages',
    columnDef: { key: 'fg_pct', label: 'FG', format: formatPct, isPct: true, group: 'shooting' } },
  { key: 'fg2_pct', label: '2P%', description: '2-point field goal percentage', category: 'Shooting Percentages',
    columnDef: { key: 'fg2_pct', label: '2P', format: formatPct, isPct: true, group: 'shooting' } },
  { key: 'fg3_pct', label: '3P%', description: '3-point field goal percentage', category: 'Shooting Percentages',
    columnDef: { key: 'fg3_pct', label: '3P', format: formatPct, isPct: true, group: 'shooting' } },
  { key: 'ft_pct', label: 'FT%', description: 'Free throw percentage', category: 'Shooting Percentages',
    columnDef: { key: 'ft_pct', label: 'FT', format: formatPct, isPct: true, group: 'shooting' } },
  { key: 'efg_pct', label: 'eFG%', description: 'Effective field goal percentage', category: 'Shooting Percentages',
    columnDef: { key: 'efg_pct', label: 'eFG', format: formatPct, isPct: true, group: 'efficiency' } },
  { key: 'ts_pct', label: 'TS%', description: 'True shooting percentage', category: 'Shooting Percentages',
    columnDef: { key: 'ts_pct', label: 'TS', format: formatPct, isPct: true, group: 'efficiency' } },
  { key: 'fg3a_pct', label: '3PAr', description: '3-point attempt rate (% of FGA that are 3s)', category: 'Shooting Percentages',
    columnDef: { key: 'fg3a_pct', label: '3PAr', format: formatPct, isPct: true, group: 'shooting' } },
  { key: 'ftr', label: 'FTr', description: 'Free throw rate (FTA / FGA)', category: 'Shooting Percentages',
    columnDef: { key: 'ftr', label: 'FTr', format: formatDec2, group: 'efficiency' } },

  // ── At Rim ──────────────────────────────────────────────────────────────
  { key: 'at_rim_fga', label: 'Rim FGA', description: 'Field goal attempts at the rim', category: 'At Rim',
    columnDef: { key: 'at_rim_fga', label: 'FGA', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shot_zones', possType: 'off' } },
  { key: 'at_rim_fgm', label: 'Rim FGM', description: 'Field goals made at the rim', category: 'At Rim',
    columnDef: { key: 'at_rim_fgm', label: 'FGM', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shot_zones', possType: 'off' } },
  { key: 'at_rim_pct', label: 'Rim FG%', description: 'Field goal % at the rim', category: 'At Rim',
    columnDef: { key: 'at_rim_pct', label: 'FG%', format: formatPct, isPct: true, group: 'shot_zones' } },
  { key: 'at_rim_freq', label: 'Rim Freq', description: '% of total FGA taken at the rim', category: 'At Rim',
    columnDef: { key: 'at_rim_freq', label: 'FREQ', format: formatPct, isPct: true, group: 'shot_zones' } },
  { key: 'at_rim_ast_pct', label: 'Rim %AST', description: '% of rim makes that were assisted', category: 'At Rim',
    columnDef: { key: 'at_rim_ast_pct', label: '%AST', format: formatPct, isPct: true, group: 'shot_zones' } },
  { key: 'at_rim_blk_pct', label: 'Rim %BLK', description: '% of rim attempts that were blocked', category: 'At Rim',
    columnDef: { key: 'at_rim_blk_pct', label: '%BLK', format: formatPct, isPct: true, group: 'shot_zones' } },

  // ── Mid Range ───────────────────────────────────────────────────────────
  { key: 'short_mid_fga', label: 'Short Mid FGA', description: 'Short mid-range FGA (floaters, short pullups)', category: 'Mid Range',
    columnDef: { key: 'short_mid_fga', label: 'SM FGA', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shot_zones', possType: 'off' } },
  { key: 'short_mid_fgm', label: 'Short Mid FGM', description: 'Short mid-range FGM', category: 'Mid Range',
    columnDef: { key: 'short_mid_fgm', label: 'SM FGM', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shot_zones', possType: 'off' } },
  { key: 'short_mid_pct', label: 'Short Mid FG%', description: 'Short mid-range FG%', category: 'Mid Range',
    columnDef: { key: 'short_mid_pct', label: 'SM%', format: formatPct, isPct: true, group: 'shot_zones' } },
  { key: 'short_mid_freq', label: 'Short Mid Freq', description: 'Short mid-range frequency', category: 'Mid Range',
    columnDef: { key: 'short_mid_freq', label: 'SM FR', format: formatPct, isPct: true, group: 'shot_zones' } },
  { key: 'long_mid_fga', label: 'Long Mid FGA', description: 'Long mid-range FGA (elbow jumpers, long 2s)', category: 'Mid Range',
    columnDef: { key: 'long_mid_fga', label: 'LM FGA', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shot_zones', possType: 'off' } },
  { key: 'long_mid_fgm', label: 'Long Mid FGM', description: 'Long mid-range FGM', category: 'Mid Range',
    columnDef: { key: 'long_mid_fgm', label: 'LM FGM', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shot_zones', possType: 'off' } },
  { key: 'long_mid_pct', label: 'Long Mid FG%', description: 'Long mid-range FG%', category: 'Mid Range',
    columnDef: { key: 'long_mid_pct', label: 'LM%', format: formatPct, isPct: true, group: 'shot_zones' } },
  { key: 'long_mid_freq', label: 'Long Mid Freq', description: 'Long mid-range frequency', category: 'Mid Range',
    columnDef: { key: 'long_mid_freq', label: 'LM FR', format: formatPct, isPct: true, group: 'shot_zones' } },

  // ── Three Point Zones ───────────────────────────────────────────────────
  { key: 'corner3_fga', label: 'Corner 3 FGA', description: 'Corner three-point attempts', category: 'Three Point Zones',
    columnDef: { key: 'corner3_fga', label: 'C3 FGA', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shot_zones', possType: 'off' } },
  { key: 'corner3_fgm', label: 'Corner 3 FGM', description: 'Corner three-point makes', category: 'Three Point Zones',
    columnDef: { key: 'corner3_fgm', label: 'C3 FGM', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shot_zones', possType: 'off' } },
  { key: 'corner3_pct', label: 'Corner 3%', description: 'Corner three-point percentage', category: 'Three Point Zones',
    columnDef: { key: 'corner3_pct', label: 'C3%', format: formatPct, isPct: true, group: 'shot_zones' } },
  { key: 'corner3_freq', label: 'Corner 3 Freq', description: 'Corner 3 frequency (% of FGA)', category: 'Three Point Zones',
    columnDef: { key: 'corner3_freq', label: 'C3 FR', format: formatPct, isPct: true, group: 'shot_zones' } },
  { key: 'arc3_fga', label: 'Arc 3 FGA', description: 'Above-the-break three-point attempts', category: 'Three Point Zones',
    columnDef: { key: 'arc3_fga', label: 'A3 FGA', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shot_zones', possType: 'off' } },
  { key: 'arc3_fgm', label: 'Arc 3 FGM', description: 'Above-the-break three-point makes', category: 'Three Point Zones',
    columnDef: { key: 'arc3_fgm', label: 'A3 FGM', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'shot_zones', possType: 'off' } },
  { key: 'arc3_pct', label: 'Arc 3%', description: 'Above-the-break three-point percentage', category: 'Three Point Zones',
    columnDef: { key: 'arc3_pct', label: 'A3%', format: formatPct, isPct: true, group: 'shot_zones' } },
  { key: 'arc3_freq', label: 'Arc 3 Freq', description: 'Above-the-break 3 frequency (% of FGA)', category: 'Three Point Zones',
    columnDef: { key: 'arc3_freq', label: 'A3 FR', format: formatPct, isPct: true, group: 'shot_zones' } },

  // ── Efficiency ──────────────────────────────────────────────────────────
  { key: 'usage', label: 'USG%', description: 'Usage rate', category: 'Efficiency',
    columnDef: { key: 'usage', label: 'USG', format: formatPct, isPct: true, group: 'usage' } },
  { key: 'ppp', label: 'PPP', description: 'Points per possession', category: 'Efficiency',
    columnDef: { key: 'ppp', label: 'PPP', format: formatDec2, group: 'efficiency' } },
  { key: 'shot_quality_avg', label: 'SQ', description: 'Shot quality average (expected FG% based on shot location)', category: 'Efficiency',
    columnDef: { key: 'shot_quality_avg', label: 'SQ', format: formatPct, isPct: true, group: 'efficiency' } },

  // ── Playmaking ──────────────────────────────────────────────────────────
  { key: 'ast', label: 'AST', description: 'Assists', category: 'Playmaking',
    columnDef: { key: 'ast', label: 'AST', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'playmaking', possType: 'off' } },
  { key: 'tov', label: 'TOV', description: 'Turnovers', category: 'Playmaking',
    columnDef: { key: 'tov', label: 'TOV', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'misc', possType: 'off' } },
  { key: 'ast_ratio', label: 'AST%', description: 'Assist ratio', category: 'Playmaking',
    columnDef: { key: 'ast_ratio', label: 'AST%', format: formatPct, isPct: true, group: 'playmaking' } },
  { key: 'tov_pct', label: 'TOV%', description: 'Turnover percentage', category: 'Playmaking',
    columnDef: { key: 'tov_pct', label: 'TOV%', format: formatPct, isPct: true, group: 'playmaking' } },
  { key: 'ast_tov_ratio', label: 'AST/TOV', description: 'Assist-to-turnover ratio', category: 'Playmaking',
    columnDef: { key: 'ast_tov_ratio', label: 'A/TO', format: formatDec1, group: 'playmaking' } },
  { key: 'two_pt_assists', label: '2PT AST', description: 'Assists on 2-point field goals', category: 'Playmaking',
    columnDef: { key: 'two_pt_assists', label: '2P AST', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'playmaking', possType: 'off' } },
  { key: 'three_pt_assists', label: '3PT AST', description: 'Assists on 3-point field goals', category: 'Playmaking',
    columnDef: { key: 'three_pt_assists', label: '3P AST', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'playmaking', possType: 'off' } },
  { key: 'assisted_2s_pct', label: '%AST 2s', description: '% of 2-point makes that were assisted', category: 'Playmaking',
    columnDef: { key: 'assisted_2s_pct', label: '%A 2s', format: formatPct, isPct: true, group: 'playmaking' } },
  { key: 'assisted_3s_pct', label: '%AST 3s', description: '% of 3-point makes that were assisted', category: 'Playmaking',
    columnDef: { key: 'assisted_3s_pct', label: '%A 3s', format: formatPct, isPct: true, group: 'playmaking' } },

  // ── Rebounding ──────────────────────────────────────────────────────────
  { key: 'reb', label: 'REB', description: 'Total rebounds', category: 'Rebounding',
    columnDef: { key: 'reb', label: 'REB', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'rebounding', possType: 'off' } },
  { key: 'oreb', label: 'OREB', description: 'Offensive rebounds', category: 'Rebounding',
    columnDef: { key: 'oreb', label: 'OREB', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'rebounding', possType: 'off' } },
  { key: 'dreb', label: 'DREB', description: 'Defensive rebounds', category: 'Rebounding',
    columnDef: { key: 'dreb', label: 'DREB', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'rebounding', possType: 'def' } },
  { key: 'off_reb_pct', label: 'ORB%', description: 'Offensive rebound percentage', category: 'Rebounding',
    columnDef: { key: 'off_reb_pct', label: 'ORB%', format: formatPct, isPct: true, group: 'rebounding' } },
  { key: 'def_reb_pct', label: 'DRB%', description: 'Defensive rebound percentage', category: 'Rebounding',
    columnDef: { key: 'def_reb_pct', label: 'DRB%', format: formatPct, isPct: true, group: 'rebounding' } },
  { key: 'self_oreb_pct', label: 'Self ORB%', description: 'Self offensive rebound rate (getting own misses)', category: 'Rebounding',
    columnDef: { key: 'self_oreb_pct', label: 'SORB%', format: formatPct, isPct: true, group: 'rebounding' } },

  // ── Defense ─────────────────────────────────────────────────────────────
  { key: 'stl', label: 'STL', description: 'Steals', category: 'Defense',
    columnDef: { key: 'stl', label: 'STL', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'defense', possType: 'def' } },
  { key: 'blk', label: 'BLK', description: 'Blocks', category: 'Defense',
    columnDef: { key: 'blk', label: 'BLK', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'defense', possType: 'def' } },
  { key: 'recovered_blocks', label: 'REC BLK', description: 'Blocks recovered (block leads to possession)', category: 'Defense',
    columnDef: { key: 'recovered_blocks', label: 'RBLK', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'defense', possType: 'def' } },
  { key: 'blocks_recovered_pct', label: 'BLK Rec%', description: '% of blocks that led to a recovered possession', category: 'Defense',
    columnDef: { key: 'blocks_recovered_pct', label: 'BRec%', format: formatPct, isPct: true, group: 'defense' } },

  // ── Fouls ───────────────────────────────────────────────────────────────
  { key: 'fouls', label: 'PF', description: 'Personal fouls committed', category: 'Fouls',
    columnDef: { key: 'fouls', label: 'PF', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'fouls', possType: 'def' } },
  { key: 'fouls_drawn', label: 'FD', description: 'Fouls drawn', category: 'Fouls',
    columnDef: { key: 'fouls_drawn', label: 'FD', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'fouls', possType: 'off' } },
  { key: 'shooting_fouls_drawn_pct', label: 'SF Drawn%', description: 'Shooting fouls drawn per FGA', category: 'Fouls',
    columnDef: { key: 'shooting_fouls_drawn_pct', label: 'SFD%', format: formatPct, isPct: true, group: 'fouls' } },
  { key: 'and1_trips', label: 'And-1', description: 'And-1 free throw trips', category: 'Fouls',
    columnDef: { key: 'and1_trips', label: 'AND1', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'fouls', possType: 'off' } },
  { key: 'loose_ball_fouls_drawn', label: 'LBF Drawn', description: 'Loose ball fouls drawn (hustle stat)', category: 'Fouls',
    columnDef: { key: 'loose_ball_fouls_drawn', label: 'LBFD', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'fouls', possType: 'off' } },
  { key: 'offensive_fouls', label: 'OFF Fouls', description: 'Offensive fouls committed', category: 'Fouls',
    columnDef: { key: 'offensive_fouls', label: 'OFF', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'fouls', possType: 'off' } },

  // ── Turnovers ───────────────────────────────────────────────────────────
  { key: 'live_ball_tov_pct', label: 'LB TOV%', description: 'Live ball turnover % (leads to fastbreaks against)', category: 'Turnovers',
    columnDef: { key: 'live_ball_tov_pct', label: 'LB%', format: formatPct, isPct: true, group: 'turnovers' } },
  { key: 'bad_pass_tov', label: 'Bad Pass', description: 'Bad pass turnovers', category: 'Turnovers',
    columnDef: { key: 'bad_pass_tov', label: 'BPTOV', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'turnovers', possType: 'off' } },
  { key: 'lost_ball_tov', label: 'Lost Ball', description: 'Lost ball turnovers', category: 'Turnovers',
    columnDef: { key: 'lost_ball_tov', label: 'LBTOV', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'turnovers', possType: 'off' } },

  // ── Second Chance ───────────────────────────────────────────────────────
  { key: 'second_chance_pts', label: '2nd Chance PTS', description: 'Points scored on second chance possessions', category: 'Second Chance',
    columnDef: { key: 'second_chance_pts', label: '2C PTS', format: formatInt, formatRate: formatDec1, isCountingStat: true, group: 'second_chance', possType: 'off' } },

  // ── Fantasy ─────────────────────────────────────────────────────────────
  { key: 'dk_fpts', label: 'DK FPTS', description: 'DraftKings fantasy points', category: 'Fantasy',
    columnDef: { key: 'dk_fpts', label: 'DK', format: formatDec1, formatRate: formatDec1, isCountingStat: true, isCalculated: true, group: 'fantasy', possType: 'off' } },
  { key: 'fd_fpts', label: 'FD FPTS', description: 'FanDuel fantasy points', category: 'Fantasy',
    columnDef: { key: 'fd_fpts', label: 'FD', format: formatDec1, formatRate: formatDec1, isCountingStat: true, isCalculated: true, group: 'fantasy', possType: 'off' } },
  { key: 'fpts_per_min', label: 'FP/M', description: 'DraftKings fantasy points per minute', category: 'Fantasy',
    columnDef: { key: 'fpts_per_min', label: 'FP/M', format: formatDec2, isCalculated: true, group: 'fantasy' } },
];

/** Default custom column keys shown when user first enables custom mode */
export const DEFAULT_CUSTOM_KEYS: string[] = [
  'minutes', 'off_poss', 'dk_fpts', 'pts', 'reb', 'ast', 'stl', 'blk', 'tov',
];

const CUSTOM_STORAGE_KEY = 'nba_rates_custom_columns';

/** Load custom column keys from localStorage */
export const loadCustomColumnKeys = (): string[] => {
  try {
    const stored = localStorage.getItem(CUSTOM_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as string[];
      const validKeys = new Set(CUSTOM_STAT_OPTIONS.map((o) => o.key));
      const filtered = parsed.filter((k) => validKeys.has(k));
      return filtered.length > 0 ? filtered.slice(0, MAX_CUSTOM_COLUMNS) : DEFAULT_CUSTOM_KEYS;
    }
  } catch { /* ignore */ }
  return DEFAULT_CUSTOM_KEYS;
};

/** Save custom column keys to localStorage */
export const saveCustomColumnKeys = (keys: string[]): void => {
  try {
    localStorage.setItem(CUSTOM_STORAGE_KEY, JSON.stringify(keys));
  } catch { /* ignore */ }
};

/** Build ColumnDef[] from selected custom keys, preserving order */
export const buildCustomColumns = (selectedKeys: string[]): ColumnDef[] => {
  const optionMap = new Map(CUSTOM_STAT_OPTIONS.map((o) => [o.key, o]));
  return selectedKeys
    .map((key) => optionMap.get(key)?.columnDef)
    .filter((col): col is ColumnDef => col != null);
};
